import axios from 'axios'
import type {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import { wait } from '@/utils/time-utils'
import { logger } from '@/utils/logger'
import {
  isDevelopment,
  getEnvVar,
  stripTrailingSlashes,
} from '@/utils/environment'
import type { IApiResponse } from '@/types'
import { API_CONFIG } from '@/types/constants'

interface IApiConfig {
  baseUrl: string
  timeout: number
  retries: number
  retryDelay: number
}

interface IRetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _retryCount?: number
}

// Lazy evaluation to ensure env vars are read at runtime, not library build time
function getBaseUrl(): string {
  const envOverride = getEnvVar('VITE_API_BASE_URL')
  if (envOverride) {
    return stripTrailingSlashes(envOverride)
  }

  const baseUrl = isDevelopment()
    ? API_CONFIG.DEV_BASE_URL
    : API_CONFIG.PROD_BASE_URL

  return stripTrailingSlashes(baseUrl)
}

// Use getter to ensure lazy evaluation at runtime
const getDefaultConfig = (): IApiConfig => ({
  baseUrl: getBaseUrl(),
  timeout: API_CONFIG.TIMEOUT,
  retries: API_CONFIG.RETRIES,
  retryDelay: API_CONFIG.RETRY_DELAY,
})

type AuthCallback = () => Promise<{
  token: string
  tenantId: string
  userId?: string
}>

class ApiClient {
  private client: AxiosInstance
  private config: IApiConfig
  private _authToken: string | null = null
  private _tenantId: string | null = null
  private _authCallback: AuthCallback | null = null

  constructor(config: Partial<IApiConfig> = {}) {
    this.config = { ...getDefaultConfig(), ...config }

    this.client = axios.create({
      baseURL: this.config.baseUrl,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    })

    this.setupInterceptors()
  }

  private async buildAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {}

    if (this._authCallback) {
      try {
        const authInfo = await this._authCallback()
        if (authInfo.token) {
          headers['Authorization'] = `Bearer ${authInfo.token}`
        }
        if (authInfo.tenantId) {
          headers['X-Tenant-ID'] = authInfo.tenantId
        }
        if (authInfo.userId) {
          headers['X-User-ID'] = authInfo.userId
        }
      } catch (error) {
        logger.error(
          'Auth callback failed',
          error instanceof Error ? error : new Error(String(error)),
          { component: 'ApiClient' }
        )
      }
    } else {
      const token = this.getAuthToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const tenantId = this.getTenantId()
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId
      }
    }

    return headers
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      async config => {
        const authHeaders = await this.buildAuthHeaders()
        Object.assign(config.headers, authHeaders)

        logger.debug(
          'API Request',
          {
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            fullURL: (config.baseURL || '') + (config.url || ''),
            data: config.data,
            params: config.params,
          },
          { component: 'ApiClient' }
        )

        return config
      },
      error => {
        logger.error(
          'API Request Error',
          error,
          { component: 'ApiClient' },
          error
        )
        return Promise.reject(error)
      }
    )

    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        logger.debug(
          'API Response',
          {
            method: response.config.method?.toUpperCase(),
            url: response.config.url,
            status: response.status,
            data: response.data,
          },
          { component: 'ApiClient' }
        )
        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as IRetryableAxiosRequestConfig

        logger.error(
          'API Error',
          {
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url,
            status: error.response?.status,
            message: error.message,
            data: error.response?.data,
          },
          { component: 'ApiClient' },
          error
        )

        if (
          this.shouldRetry(error) &&
          !originalRequest._retry &&
          (originalRequest._retryCount ?? 0) < this.config.retries
        ) {
          originalRequest._retry = true
          originalRequest._retryCount = (originalRequest._retryCount ?? 0) + 1

          logger.info(
            'Retrying request',
            {
              attempt: originalRequest._retryCount ?? 0,
              maxRetries: this.config.retries,
            },
            { component: 'ApiClient' }
          )

          await wait(
            this.config.retryDelay * (originalRequest._retryCount ?? 1)
          )
          return this.client(originalRequest)
        }

        return Promise.reject(this.createApiError(error))
      }
    )
  }

  private shouldRetry(error: AxiosError): boolean {
    const method = error.config?.method?.toLowerCase()
    const isIdempotent =
      method === 'get' || method === 'head' || method === 'options'

    if (!isIdempotent) {
      return false
    }

    return (
      !error.response ||
      (error.response.status >= 500 && error.response.status < 600)
    )
  }

  private getAuthToken(): string | null {
    return this._authToken
  }

  private getTenantId(): string | null {
    return this._tenantId
  }

  private createApiError(error: AxiosError): ApiError {
    const response = error.response?.data as IApiResponse<unknown> | undefined

    return new ApiError(
      response?.message || error.message || 'An unknown error occurred',
      error.response?.status || 0,
      response?.errors || [],
      response
    )
  }

  // Generic request methods
  async get<T>(
    url: string,
    params?: Record<string, unknown>
  ): Promise<IApiResponse<T>> {
    const response = await this.client.get<IApiResponse<T>>(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: unknown): Promise<IApiResponse<T>> {
    const response = await this.client.post<IApiResponse<T>>(url, data)
    return response.data
  }

  async put<T>(url: string, data?: unknown): Promise<IApiResponse<T>> {
    const response = await this.client.put<IApiResponse<T>>(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<IApiResponse<T>> {
    const response = await this.client.delete<IApiResponse<T>>(url)
    return response.data
  }

  // Update configuration
  updateConfig(config: Partial<IApiConfig>): void {
    this.config = { ...this.config, ...config }
    this.client.defaults.baseURL = this.config.baseUrl
    this.client.defaults.timeout = this.config.timeout
  }

  setAuthToken(token: string): void {
    try {
      this._authToken = token
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } catch (error) {
      logger.error(
        'Failed to set auth token',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'ApiClient' }
      )
    }
  }

  clearAuth(): void {
    try {
      this._authToken = null
      this._tenantId = null
      this._authCallback = null
      delete this.client.defaults.headers.common['Authorization']
      delete this.client.defaults.headers.common['X-Tenant-ID']
    } catch (error) {
      logger.error(
        'Failed to clear auth',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'ApiClient' }
      )
    }
  }

  setTenantId(tenantId: string): void {
    try {
      this._tenantId = tenantId
      this.client.defaults.headers.common['X-Tenant-ID'] = tenantId
    } catch (error) {
      logger.error(
        'Failed to set tenant ID',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'ApiClient' }
      )
    }
  }

  setAuthCallback(callback: AuthCallback | null): void {
    try {
      this._authCallback = callback

      if (callback) {
        delete this.client.defaults.headers.common['Authorization']
        delete this.client.defaults.headers.common['X-Tenant-ID']
        delete this.client.defaults.headers.common['X-User-ID']
        this._authToken = null
        this._tenantId = null
      }

      logger.info(
        'Auth callback set',
        { hasCallback: !!callback },
        { component: 'ApiClient' }
      )
    } catch (error) {
      logger.error(
        'Failed to set auth callback',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'ApiClient' }
      )
    }
  }

  setBaseUrl(baseUrl: string): void {
    try {
      this.config.baseUrl = baseUrl
      this.client.defaults.baseURL = baseUrl
      logger.info(
        'API base URL updated',
        { baseUrl },
        { component: 'ApiClient' }
      )
    } catch (error) {
      logger.error(
        'Failed to set base URL',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'ApiClient' }
      )
    }
  }

  async getAuthHeaders(): Promise<Record<string, string>> {
    return this.buildAuthHeaders()
  }

  getBaseUrl(): string {
    return this.config.baseUrl
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public _status: number = 0,
    public _errors: string[] = [],
    public _response?: IApiResponse<unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isNetworkError(): boolean {
    return this._status === 0
  }

  get isServerError(): boolean {
    return this._status >= 500
  }

  get isClientError(): boolean {
    return this._status >= 400 && this._status < 500
  }

  get isUnauthorized(): boolean {
    return this._status === 401
  }

  get isForbidden(): boolean {
    return this._status === 403
  }

  get isNotFound(): boolean {
    return this._status === 404
  }
}

export { ApiClient }
export const apiClient = new ApiClient()
export const createApiClient = (config: Partial<IApiConfig>) =>
  new ApiClient(config)

export default apiClient
